import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, from, map, of, switchMap, tap } from 'rxjs';

import { Router } from '@angular/router';
import { AuthenticationControllerService, ResponseModelAuthenticationDTO } from '@chicilon/angular-user-service';
import { LocalStorageEnum } from '@core/enum/local-storage';
import { SessionCacheRegistry } from '@core/services/session-cache-registry.service';
import {
  CONTRACT_EXPORT_STORAGE_KEYS,
  DEVICE_EXPORT_STORAGE_KEYS,
  RENTAL_EXPORT_STORAGE_KEYS
} from '@modules/exploit/shared/constants/report-export-key';
import { NgxPermissionsService } from 'ngx-permissions';
import { loadAuthFromStorage, login, loginFailed, loginSuccess, logout } from './auth.actions';

@Injectable()
export class AuthEffects {
  actions$ = inject(Actions);
  router = inject(Router);
  authService = inject(AuthenticationControllerService);
  private permissionsService = inject(NgxPermissionsService);
  private sessionCaches = inject(SessionCacheRegistry);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      exhaustMap(action =>
        from(
          this.authService.login({
            username: action.username,
            password: action.password
          })
        ).pipe(
          switchMap((res: ResponseModelAuthenticationDTO) => {
            return of(
              loginSuccess({
                accessToken: res.data?.access_token?.token,
                refreshToken: res.data?.refresh_token?.token,
                expiredToken: res.data?.access_token?.expired_at?.toString(),
                user: res.data?.user,
                permissionCodes: res.data?.permission_codes
              })
            ).pipe(
              tap(res => {
                this.permissionsService.loadPermissions(res.permissionCodes || []);
                // navigateByUrl trả về Promise và sẽ REJECT khi lazy chunk của route
                // đích không tải được (ví dụ chunk hash cũ đã bị xoá sau khi deploy).
                // Trước đây promise này không được catch nên lỗi chỉ hiện
                // "Uncaught (in promise)" và người dùng kẹt lại trang login.
                // ChunkErrorRecoveryService lắng nghe NavigationError để tự phục hồi;
                // ở đây chỉ cần đảm bảo promise không bị bỏ rơi.
                this.router.navigateByUrl('/').catch(error => {
                  console.error('[login] Điều hướng sau khi đăng nhập thất bại:', error);
                });
              })
            );
          }),
          catchError(error => {
            return of(loginFailed({ error: error?.error?.error?.code_string || 'LOGIN_FAILED' }));
          })
        )
      )
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginSuccess),
        tap(({ accessToken, refreshToken, expiredToken, user }) => {
          if (accessToken) localStorage.setItem(LocalStorageEnum.ACCESS_TOKEN, accessToken);
          if (refreshToken) localStorage.setItem(LocalStorageEnum.REFRESH_TOKEN, refreshToken);
          if (expiredToken) localStorage.setItem(LocalStorageEnum.EXPIRED_TOKEN, expiredToken);
          localStorage.setItem(LocalStorageEnum.USER, JSON.stringify(user));
        })
      ),
    { dispatch: false }
  );

  loadAuthFromStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAuthFromStorage),
      map(() => {
        const accessToken = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');
        const expiredToken = localStorage.getItem('expiredToken'); // nếu cần
        const userJson = localStorage.getItem('user');
        const user = userJson ? JSON.parse(userJson) : null;

        if (accessToken && refreshToken && user && expiredToken) {
          return loginSuccess({
            accessToken,
            refreshToken,
            expiredToken,
            user
          });
        } else {
          return logout();
        }
      })
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        tap(() => {
          localStorage.removeItem(LocalStorageEnum.ACCESS_TOKEN);
          localStorage.removeItem(LocalStorageEnum.REFRESH_TOKEN);
          localStorage.removeItem(LocalStorageEnum.EXPIRED_TOKEN);
          localStorage.removeItem(LocalStorageEnum.USER);
          [...DEVICE_EXPORT_STORAGE_KEYS, ...RENTAL_EXPORT_STORAGE_KEYS, ...CONTRACT_EXPORT_STORAGE_KEYS].forEach(
            key => {
              localStorage.removeItem(key);
            }
          );

          this.sessionCaches.resetAll();

          this.router.navigate(['/login']);
        })
      ),
    { dispatch: false }
  );
}
